/**
 * LLM / stream çıktısını markdown’a yaklaştırır (PatientAnalyze, AI Chat, vb.).
 * Başlık metinleri sabit listesi kullanılmaz; yalnızca yapısal kalıplar onarılır.
 */

import { convertGfmTablesToLists } from "./gfmTableToList";

function repairBrokenBoldFragments(s: string): string {
  let prev = "";
  let t = s;
  for (let i = 0; i < 12 && t !== prev; i += 1) {
    prev = t;
    t = t.replace(/\*\*([^*\n]+?)\n\n\*\*([A-ZÇĞİÖŞÜ])/g, "**$1**\n\n$2");
    t = t.replace(/\*\*([^*\n]+?)\n\*\*([A-ZÇĞİÖŞÜ])/g, "**$1**\n\n$2");
  }
  return t;
}

function repairListLineMissingOpenBold(s: string): string {
  return s.replace(
    /^(\s*(?:[-*+]|\d+\.)\s+)(?!\*\*)(.+)$/gm,
    (full, prefix: string, rest: string) => {
      const t = rest.trimEnd();
      if (!/:\*\*\s*$/.test(t)) return full;
      const label = t.replace(/:\*\*\s*$/, "");
      if (!label.trim()) return full;
      if (label.includes("**")) return full;
      return `${prefix}**${label}:**`;
    }
  );
}

function repairOrphanColonBoldLines(s: string): string {
  return s.replace(
    /^([ \t]*)([A-Za-zÇĞİÖŞÜçğıöşüİı0-9][^*\n:]{0,120}):\*\*\s*$/gm,
    "$1* **$2:**"
  );
}

function normalizeUnicodeBullets(s: string): string {
  return s.replace(/^(\s*)[\u2022\u00B7\u25AA\u25CF]\s+/gm, "$1* ");
}

export function normalizeStreamingAiMarkdown(raw: string): string {
  let t = raw.replace(/\r\n/g, "\n").trim();
  if (!t) return t;

  t = convertGfmTablesToLists(t);

  // SSE keepalive çöpü `##` ile yapışık gelirse başlık tanınmaz; satır başındaki # öncesi çöpü at
  t = t.replace(/^[^\n#]*(?=(#{1,6}\s))/, "");

  // "...gerekir.## Başlık" — stream birleşimi; başlık için boş satır gerekir
  t = t.replace(/([.!?…)])(\s*)(#{1,6}\s)/g, "$1\n\n$3");

  // Başlık satırından hemen sonra tek satır sonu ile gelen gövdeyi paragrafa ayır
  t = t.replace(/^(#{1,6}\s[^\n]+)\n([A-Za-zÇĞİÖŞÜçğıöşüİı0-9*])/gm, "$1\n\n$2");

  t = normalizeUnicodeBullets(t);
  t = repairBrokenBoldFragments(t);
  t = repairListLineMissingOpenBold(t);

  t = t.replace(/\.(---+)(?=\s|\n|$)/g, ".\n\n$1\n");

  t = t.replace(/([.!?)\]ıİ])\s*(---+)(?=\s|$|\n)/g, "$1\n\n$2\n");
  t = t.replace(/([^\s\n])(---+)(?=[A-ZÇĞİÖŞÜ])/g, "$1\n\n$2\n\n");

  t = t.replace(/(---+)\s*(#{1,6}\s)/g, "$1\n\n$2");

  t = t.replace(/([^\n#])###(?!#)\s/g, "$1\n\n### ");
  t = t.replace(/([^\n])####\s/g, "$1\n\n#### ");

  // "...izlenmelidir.**2. Kardiyak Takip:**" — numaralı alt başlık cümleye yapışık (stream)
  t = t.replace(/([.!?])(\*\*[1-9]\d*\.\s+)/g, "$1\n\n$2");

  t = t.replace(/([a-zçğıöşüıİ0-9!?.])\*\*([A-ZÇĞİÖŞÜ])/g, "$1\n\n**$2");

  t = t.replace(/([a-zçğıöşüıİ])(Bu\s+hasta\b)/g, "$1\n\n$2");
  t = t.replace(/([a-zçğıöşüıİ])(Aşağıdaki\s)/g, "$1\n\n$2");
  t = t.replace(/([a-zçğıöşüıİ])(Öne\s+Çıkan\s)/g, "$1\n\n$2");

  t = t.replace(/(verilmelidir)\.(\*)(\s*)(\*\*)/gi, "$1.\n\n* $4");

  t = t.replace(/([a-zçğıöşüıİ])(\d+\.\s+(?:\*\*|[A-ZÇĞİÖŞÜ]))/g, "$1\n\n$2");

  t = t.replace(/([a-zçğıöşüıİ])(\d+\.)((?:\*\*)|[A-ZÇĞİÖŞÜ])/g, (_, a: string, b: string, c: string) =>
    c === "**" ? `${a}\n\n${b}${c}` : `${a}\n\n${b} ${c}`
  );

  t = t.replace(
    /([a-zçğıöşüıİ])(\d+\.\s+[A-Za-zÇĞİÖŞÜçğıöşüİı][^*\n]{0,200}?:\*\*)/g,
    "$1\n\n$2"
  );

  t = t.replace(/\*\*(\s+)(\*\*([1-9]\d*)\.\s+[A-ZÇĞİÖŞÜ])/g, "**\n\n$2");

  t = t.replace(/\*\*(\s+)(\*{1,2}\s+\*\*[A-Za-zÇĞİÖŞÜçğıöşüİı])/g, "**\n\n$2");

  t = t.replace(/([a-zçğıöşüıİ0-9!?.])(\s*\*\s+\*\*)/g, "$1\n\n* ");

  t = t.replace(/([a-zçğıöşüıİ0-9!?])\*(\s+[*A-ZÇĞİÖŞÜ])/g, "$1\n\n*$2");

  t = t.replace(/(^|\n)\*(?=[A-Za-zÇĞİÖŞÜçğıöşüİı0-9"'«])/g, "$1* ");

  t = t.replace(/:\*\*\s+([A-ZÇĞİÖŞÜ])/g, ":**\n\n$1");

  t = repairOrphanColonBoldLines(t);

  t = t.replace(/([a-zçğıöşüıİ]{2,}):([A-ZÇĞİÖŞÜ][a-zçğıöşü]{2,})/g, "$1:\n\n$2");

  t = t.replace(/([.!?])(Hasta[,]\s)/g, "$1\n\n$2");

  t = t.replace(/([a-zçğıöşüıİ0-9)])(\.)(Bu değerlendirme yapay zeka)/gi, "$1.\n\n$3");
  t = t.replace(/(\.\s+)(Bu değerlendirme yapay zeka\b)/gi, ".\n\n$2");

  t = t.replace(/([a-zçğıöşüıİ)])(\.)([A-ZÇĞİÖŞÜ][a-zçğıöşü]{3,})/g, "$1.\n\n$3");

  t = t.replace(/\n\*\*\s*\n\*\*\s*\n/g, "\n\n");

  t = t.replace(/^\s*\*\*\s*$/gm, "");

  t = repairListLineMissingOpenBold(t);

  t = t.replace(/\.(---+)(?=\s|\n|$)/g, ".\n\n$1\n");
  t = t.replace(/\*\*(\s+)(\*\*([1-9]\d*)\.\s+[A-ZÇĞİÖŞÜ])/g, "**\n\n$2");
  t = t.replace(/\*\*(\s+)(\*{1,2}\s+\*\*[A-Za-zÇĞİÖŞÜçğıöşüİı])/g, "**\n\n$2");
  t = t.replace(/([a-zçğıöşüıİ])(\d+\.)((?:\*\*)|[A-ZÇĞİÖŞÜ])/g, (_, a: string, b: string, c: string) =>
    c === "**" ? `${a}\n\n${b}${c}` : `${a}\n\n${b} ${c}`
  );

  t = t.replace(/(---+)\s*(#{1,6}\s)/g, "$1\n\n$2");

  t = t.replace(
    /([a-zçğıöşüıİ])(\d+\.\s+[A-Za-zÇĞİÖŞÜçğıöşüİı][^*\n]{0,200}?:\*\*)/g,
    "$1\n\n$2"
  );

  t = t.replace(/([.!?])(\*\*[1-9]\d*\.\s+)/g, "$1\n\n$2");

  t = repairListLineMissingOpenBold(t);

  t = t.replace(/\n{3,}/g, "\n\n");

  return t.trim();
}

/** @deprecated Tercihen `normalizeStreamingAiMarkdown` kullanın */
export const normalizeAiAnalysisMarkdown = normalizeStreamingAiMarkdown;
